/**
 * Per-tab conveniences only. Nothing here is needed for correctness: a
 * blocked or empty storage just means a fresh visitor id and no remembered
 * identity, so every access is wrapped.
 */

function read(store: 'local' | 'session', key: string): string | null {
  try {
    return (store === 'local' ? window.localStorage : window.sessionStorage).getItem(key);
  } catch {
    return null;
  }
}

function write(store: 'local' | 'session', key: string, value: string | null): void {
  try {
    const s = store === 'local' ? window.localStorage : window.sessionStorage;
    if (value === null) s.removeItem(key);
    else s.setItem(key, value);
  } catch {
    /* storage blocked */
  }
}

function randomId(): string {
  const bytes = new Uint8Array(18);
  crypto.getRandomValues(bytes);
  let s = '';
  for (const b of bytes)
    s += 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'[b & 63];
  return s;
}

/** One id per tab visit: funnel steps and the abandonment lead group by it. */
export function visitorId(slug: string): string {
  const key = `velora.booking.visitor.${slug}`;
  const existing = read('session', key);
  if (existing && /^[A-Za-z0-9_-]{16,64}$/.test(existing)) return existing;
  const id = randomId();
  write('session', key, id);
  return id;
}

/** The identity token for this tab (token link or verified code). */
export function loadIdentity(slug: string): string | null {
  return read('session', `velora.booking.identity.${slug}`);
}

export function saveIdentity(slug: string, token: string | null): void {
  write('session', `velora.booking.identity.${slug}`, token);
}

export type ThemeChoice = 'dark' | 'light' | 'system';

export function loadTheme(): ThemeChoice {
  const v = read('local', 'velora.booking.theme');
  return v === 'dark' || v === 'light' ? v : 'system';
}

export function saveTheme(v: ThemeChoice): void {
  write('local', 'velora.booking.theme', v === 'system' ? null : v);
}
